import { Translate } from 'next-translate';
import {
  OneSchemaTemplateColumnToAdd as BaseOneSchemaTemplateColumnToAdd,
  OneSchemaTemplateColumnToUpdate,
  OneSchemaTemplateOverrides,
} from '@oneschema/react';

import { BaseUnitOfMeasureProps } from '@components/company/UnitOfMeasure';
import { CustomColumnHeader, Lot } from 'types';
import { SUGGESTED_PRICE_ENHANCEMENT_PHASE_DEFAULT } from '@enums/flags';
import { applyColumnLabelsToTemplateOverrides } from '@features/event-level-column-rename';
import { shouldAcceptSuggestedPriceImportColumn } from '@features/suggested-price-column/utils/should-accept-suggested-price-import-column';
import {
  BOOLEAN,
  BOOLEAN_FALSE_LABEL,
  BOOLEAN_TRUE_LABEL,
  CATEGORY_TEMPLATE_COLUMN_KEY,
  INTERNAL_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY,
  IS_REQUIRED_TEMPLATE_COLUMN_KEY,
  LINE_ITEM_ID_TEMPLATE_COLUMN_KEY,
  LINE_ITEM_NAME_TEMPLATE_COLUMN_KEY,
  LOT_NAME_TEMPLATE_COLUMN_KEY,
  NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
  NUMBER,
  PICKLIST,
  PICKLIST_OPTION_COLOR_GRAY,
  PRICE_PER_UNIT_TEMPLATE_COLUMN_KEY,
  QUANTITY_OF_UNITS_TEMPLATE_COLUMN_KEY,
  SUGGESTED_PRICE_TEMPLATE_COLUMN_KEY,
  SUPPLIER_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY,
  UNIT_OF_MEASURE_TEMPLATE_COLUMN_KEY,
} from '@components/bid_request/details/constants';
import { createTemplateColumn } from './createTemplateColumn';
import type { LineItemColumnLabels } from '@features/event-level-column-rename';

// OneSchema accepts alternative_names at runtime but the SDK type doesn't include it
// https://docs.oneschema.co/docs/per-customer-overrides-v2#oneschemapicklistoption
type OneSchemaTemplateColumnToAdd = BaseOneSchemaTemplateColumnToAdd & { alternative_names?: string[] };

/**
 * Creates a template column for Quantity of Units.
 * @param t
 * @return {OneSchemaTemplateColumnToAdd}
 */
const quantityOfUnitsColumn = (t: Translate): OneSchemaTemplateColumnToAdd =>
  createTemplateColumn(
    t('line_items.quantity_of_units'),
    QUANTITY_OF_UNITS_TEMPLATE_COLUMN_KEY,
    true,
    true,
    NUMBER,
    {
      min_num: 1
    }
  );

/**
 * Creates a template column for Category. Added for price updates only (a seeded
 * price-update attribute column like the part-number columns).
 * @param t
 * @return {OneSchemaTemplateColumnToAdd}
 */
const categoryColumn = (t: Translate): OneSchemaTemplateColumnToAdd =>
  createTemplateColumn(
    t('general.category'),
    CATEGORY_TEMPLATE_COLUMN_KEY,
    false
  );

/**
 * Creates a template column for Suggested Price.
 *
 * Labelled "Suggested Price" to match the buyer HoT header and the buyer XLSX export header
 * verbatim, so a downloaded sheet auto-maps on re-upload. "Suggested Price per Unit" is carried
 * as an alternative name to match the other price columns' pattern.
 *
 * min_num 0 rejects negatives inside the OneSchema modal. Zero is intentionally allowed: it is a
 * real $0 buyer override, and the buyer grid already persists '0' when a cell is cleared
 * (see getLineItemChangesPayload). Blank is the "let AI pricing suggest it" signal and is
 * handled in createLineItem, not here — OneSchema cannot express "blank or > 0".
 * @param t
 * @return {OneSchemaTemplateColumnToAdd}
 */
const suggestedPriceColumn = (t: Translate): OneSchemaTemplateColumnToAdd => ({
  ...createTemplateColumn(
    t('line_items.suggested_price'),
    SUGGESTED_PRICE_TEMPLATE_COLUMN_KEY,
    false,
    false,
    NUMBER,
    { min_num: 0 }
  ),
  alternative_names: [t('line_items.suggested_price_per_unit')],
  description: t('bid_request.line_items.oneschema.suggested_price_description'),
});

const requiredColumn = (t: Translate): OneSchemaTemplateColumnToAdd => ({
  ...createTemplateColumn(
    t('general.required'),
    IS_REQUIRED_TEMPLATE_COLUMN_KEY,
    false,
    false,
    BOOLEAN,
    { false_label: BOOLEAN_FALSE_LABEL, true_label: BOOLEAN_TRUE_LABEL }
  ),
  description: t('bid_request.line_items.oneschema.required_description'),
});

export interface GetTemplateOverridesOptions {
  canImportPostSubmission?: boolean;
  companyColumnNameOverrides?: Record<string, string> | null;
  hasActiveSubmissions?: boolean;
  isPriceUpdate?: boolean;
  lineItemColumnLabels?: LineItemColumnLabels | null;
  lots?: Lot[];
  suggestedPriceEnhancementPhase?: number;
  t: Translate;
  uomFlag?: boolean;
  uoms?: BaseUnitOfMeasureProps[];
  useSuggestedPricing?: boolean;
}

/**
 * Returns the template overrides.
 * A price-update import relabels price_per_unit to "Current Agreed Price per Unit" and omits the
 * "Negotiation Starting Price" column; every other event type relabels it to "Baseline Price per
 * Unit" and adds that column. Both write columns_to_update for the same key, so the two branches
 * are mutually exclusive. This applies to the pre-submission template only; the post-submission
 * branch returns earlier and is unaffected.
 * @param options {GetTemplateOverridesOptions}
 * @param options.canImportPostSubmission {Boolean} import into an event that can accept post-submission edits
 * @param options.companyColumnNameOverrides {Record<string, string>} the organisation's
 *   i18n-key-to-label overrides; kept as an upload alternative alongside the event label so a
 *   sheet downloaded under the organisation wording still auto-matches after an event override
 *   supersedes it
 * @param options.hasActiveSubmissions {Boolean} event already has supplier submissions
 * @param options.lineItemColumnLabels {LineItemColumnLabels} the event's canonical-id-to-label
 *   map; renamed columns are relabeled in the template as the final step, with every prior name
 *   kept as an alternative so default-named and organisation-named sheets still auto-match
 * @param options.isPriceUpdate {Boolean} price-update event: relabel price_per_unit to "Current
 *   Agreed Price per Unit" (required) and add optional Supplier/Internal Part Number and Category
 *   columns. Mapped values become descriptive custom columns and are supplier-visible by design
 *   (they let suppliers confirm their own part numbers on the bid).
 * @param options.lots {Lot[]} event lots (for hidden custom columns, post-submission)
 * @param options.suggestedPriceEnhancementPhase {Number} suggested-price LD phase flag
 * @param options.t {Translate}
 * @param options.uomFlag {Boolean} unitOfMeasurementStandardization flag
 * @param options.uoms {BaseUnitOfMeasureProps[]}
 * @param options.useSuggestedPricing {Boolean} event uses suggested pricing
 *   (!request.anchor_pricing_disabled)
 * @return {OneSchemaTemplateOverrides}
 */
export const getTemplateOverrides = ({
  canImportPostSubmission = false,
  companyColumnNameOverrides,
  hasActiveSubmissions = false,
  isPriceUpdate = false,
  lineItemColumnLabels,
  lots = [],
  suggestedPriceEnhancementPhase = SUGGESTED_PRICE_ENHANCEMENT_PHASE_DEFAULT,
  t,
  uomFlag = false,
  uoms = [],
  useSuggestedPricing = false,
}: GetTemplateOverridesOptions): OneSchemaTemplateOverrides => {
  const usePostSubmissionProcess = canImportPostSubmission && hasActiveSubmissions;
  // Same gate processOneSchemaLineItems uses to decide whether to read the mapped value, so the
  // column can never be offered without being honoured (or honoured without being offered).
  const acceptSuggestedPrice = shouldAcceptSuggestedPriceImportColumn({
    isPriceUpdate,
    suggestedPriceEnhancementPhase,
    useSuggestedPricing,
  });
  const templateOverrides: OneSchemaTemplateOverrides = {
    columns_to_add: [],
    columns_to_remove: [],
    columns_to_update: [],
  };

  // Post-submission: lock structural columns so they appear in the UI but can't be modified.
  // Only Baseline Price per Unit, Line Item ID, and hidden custom columns are editable.
  if (usePostSubmissionProcess) {
    const lockedColumnDescription = t('bid_request.line_items.oneschema.column_locked_post_submission');

    // === STRUCTURAL / NOT UPDATEABLE COLUMNS ===
    // Lock structural columns — visible but not editable after submission
    // These are columns that are in OneSchema template defined in their platform.
    // https://arkestro-development.oneschema.co/templates/5107442
    const structuralColumns = [
      LOT_NAME_TEMPLATE_COLUMN_KEY,
      LINE_ITEM_NAME_TEMPLATE_COLUMN_KEY,
      UNIT_OF_MEASURE_TEMPLATE_COLUMN_KEY,
    ];
    structuralColumns.forEach(key => {
      templateOverrides.columns_to_update?.push({
        description: lockedColumnDescription,
        is_locked: true,
        is_required: false,
        key,
        must_exist: false,
      } as OneSchemaTemplateColumnToUpdate);
    });

    // === BASELINE PRICE PER UNIT ===
    // Rename price_per_unit to "Baseline Price per Unit" — not required post-submission
    templateOverrides.columns_to_update?.push({
      alternative_names: [t('general.price_per_unit')],
      description: t('bid_request.line_items.oneschema.baseline_price_description'),
      is_required: false,
      key: PRICE_PER_UNIT_TEMPLATE_COLUMN_KEY,
      label: t('general.baseline_price_per_unit'),
      must_exist: false,
    } as OneSchemaTemplateColumnToUpdate);

    // === QUANTITY OF UNITS (locked) ===
    // Add Quantity of Units so the spreadsheet column auto-maps, locked because
    // quantities cannot change after a supplier has submitted.
    templateOverrides.columns_to_add?.push({
      ...createTemplateColumn(
        t('line_items.quantity_of_units'),
        QUANTITY_OF_UNITS_TEMPLATE_COLUMN_KEY,
        false,
        false,
        NUMBER,
      ),
      description: lockedColumnDescription,
      is_locked: true,
    } as OneSchemaTemplateColumnToAdd);

    // === NEGOTIATION STARTING PRICE ===
    // Locked column so it auto-maps but can't be edited.
    templateOverrides.columns_to_add?.push({
      ...createTemplateColumn(
        t('line_items.negotiation_price_per_unit'),
        NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
        false,
        false,
        NUMBER,
      ),
      description: lockedColumnDescription,
      is_locked: true,
    } as OneSchemaTemplateColumnToAdd);

    // === SUGGESTED PRICE ===
    // The buyer export includes Suggested Price post-submission, so the column must exist here or
    // a re-uploaded sheet would land it as an unmapped descriptive custom column. Locked, exactly
    // like Negotiation Starting Price above: it auto-maps, shows as non-editable, and
    // handlePostSubmissionImport never reads it — suggested prices cannot change after a
    // supplier has submitted.
    if (acceptSuggestedPrice) {
      templateOverrides.columns_to_add?.push({
        ...suggestedPriceColumn(t),
        description: lockedColumnDescription,
        is_locked: true,
      } as OneSchemaTemplateColumnToAdd);
    }

    // === REQUIRED (locked — the post-submission update ignores it) ===
    templateOverrides.columns_to_add?.push({
      ...requiredColumn(t),
      description: lockedColumnDescription,
      is_locked: true,
    } as OneSchemaTemplateColumnToAdd);

    // === HIDDEN CUSTOM COLUMNS ===
    // Add hidden (non-supplier-visible) custom columns as template columns so they auto-map.
    // Dedupe on the sanitized key to avoid duplicate template columns when display texts
    // differ only in punctuation (e.g. "Notes!" and "Notes?" both produce "custom_notes").
    const addedKeys = new Set<string>();
    lots.forEach((lot: Lot) => {
      (lot.custom_column_headers || []).forEach((header: CustomColumnHeader) => {
        if (!header.is_supplier_visible && header.display_text) {
          const key = `custom_${header.display_text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`;
          if (addedKeys.has(key)) return;

          addedKeys.add(key);
          templateOverrides.columns_to_add?.push({
            ...createTemplateColumn(header.display_text, key, false, false),
            description: t('bid_request.line_items.oneschema.hidden_column_description'),
          } as OneSchemaTemplateColumnToAdd);
        }
      });
    });

    // === ARKESTRO INTERNAL ID ===
    // Add Arkestro Internal ID column — required for matching, locked so users can't modify IDs.
    // Alternative names ensure old spreadsheets with "Line Item ID" headers still auto-map.
    templateOverrides.columns_to_add?.push({
      ...createTemplateColumn(
        t('general.arkestro_internal_id'),
        LINE_ITEM_ID_TEMPLATE_COLUMN_KEY,
        true,
        true,
        NUMBER,
      ),
      alternative_names: ['Line Item ID', 'Line Item Id'],
      description: t('bid_request.line_items.oneschema.arkestro_internal_id_description'),
      is_locked: true,
    } as OneSchemaTemplateColumnToAdd);

    return applyColumnLabelsToTemplateOverrides({ companyColumnNameOverrides, labels: lineItemColumnLabels, overrides: templateOverrides, t });
  }

  // Pre-submission: full template
  templateOverrides.columns_to_update?.push(
    createTemplateColumn(
      t('general.line_item_name'),
      LINE_ITEM_NAME_TEMPLATE_COLUMN_KEY,
      true
    )
  );

  if (isPriceUpdate) {
    // must_exist forces the column to be mapped before the buyer can proceed (the required (*)
    // gate), and is_required forbids blank cells once mapped. Both are needed to match the
    // enforcement of Unit of Measure / Quantity of Units; is_required alone renders no gate.
    templateOverrides.columns_to_update?.push({
      alternative_names: [t('general.price_per_unit')],
      is_required: true,
      key: PRICE_PER_UNIT_TEMPLATE_COLUMN_KEY,
      label: t('general.current_agreed_price_per_unit'),
      must_exist: true,
    } as OneSchemaTemplateColumnToUpdate);

    // Optional descriptive part-number and Category columns. Labeled with the plain default names
    // (no "…/SKU" suffix) so they match the server-seeded default columns and a filled sample-
    // download sheet auto-maps directly on the label.
    templateOverrides.columns_to_add?.push(
      createTemplateColumn(
        t('general.supplier_part_number'),
        SUPPLIER_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY,
        false
      ),
      createTemplateColumn(
        t('general.internal_part_number'),
        INTERNAL_PART_NUMBER_SKU_TEMPLATE_COLUMN_KEY,
        false
      ),
      categoryColumn(t)
    );
  } else {
    // Rename the built-in price_per_unit column to "Baseline Price per Unit" and add
    // "Price per Unit" as an alternative name so old spreadsheets still auto-map.
    templateOverrides.columns_to_update?.push({
      alternative_names: [t('general.price_per_unit')],
      key: PRICE_PER_UNIT_TEMPLATE_COLUMN_KEY,
      label: t('general.baseline_price_per_unit'),
    } as OneSchemaTemplateColumnToUpdate);

    templateOverrides.columns_to_add?.push(
      createTemplateColumn(
        t('line_items.negotiation_price_per_unit'),
        NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
        false,
        false,
        NUMBER,
        { min_num: 0 }
      )
    );
  }

  // Slotted after the baseline/NSP columns and before Quantity of Units to mirror the buyer
  // export order (BUYER_ITEM_TYPES_PHASED in getExportColumnNames).
  if (acceptSuggestedPrice) {
    templateOverrides.columns_to_add?.push(suggestedPriceColumn(t));
  }

  templateOverrides.columns_to_add?.push(quantityOfUnitsColumn(t));

  templateOverrides.columns_to_add?.push(requiredColumn(t));

  if (uomFlag) {
    templateOverrides.columns_to_update?.push({
      ...createTemplateColumn(
        t('unit_of_measure.units_of_measure'),
        UNIT_OF_MEASURE_TEMPLATE_COLUMN_KEY,
        true,
        true,
        PICKLIST,
        {
          picklist_options: uoms.map(uom => ({
            alternative_names: [uom.unit_name],
            color: PICKLIST_OPTION_COLOR_GRAY,
            description: uom.unit_name,
            value: uom.internal_id,
          })),
        }
      ),
    } as OneSchemaTemplateColumnToUpdate);
  }

  return applyColumnLabelsToTemplateOverrides({ companyColumnNameOverrides, labels: lineItemColumnLabels, overrides: templateOverrides, t });
};
